import { supabase } from '@/lib/supabase';
import { callApi } from '@/utils/api';
import { getTeamMembersQueryKey } from '@/utils/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type TeamRole = 'OWNER' | 'ADMIN' | 'MEMBER';

export type TeamMember = {
  team_id: string;
  user_id: string;
  role: TeamRole;
  user?: {
    email: string;
    name: string;
  };
};

export type TeamInvite = {
  id: string;
  team_id: string;
  email: string;
  name: string;
  role: TeamRole;
  created_at: string;
  expires_at: string;
};

export type TeamMembersResponse = {
  users: TeamMember[];
  invites: TeamInvite[];
};

export const useTeamMembers = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: getTeamMembersQueryKey(),
    queryFn: async (): Promise<TeamMembersResponse> => {
      const { data, error } = await callApi(supabase, '/teams');
      if (error) throw error;

      return {
        users: data.users,
        invites: data.invites,
      };
    },
  });

  const updateTeamMember = useMutation({
    mutationFn: async ({
      userId,
      role,
    }: {
      userId: string;
      role: TeamRole;
    }) => {
      const { error } = await callApi(
        supabase,
        `/teams/members/${userId}/role`,
        {
          method: 'PUT',
          data: { role },
        },
      );
      if (error) throw error;
    },
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: getTeamMembersQueryKey() }),
  });

  const deleteTeamMember = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await callApi(supabase, `/teams/members/${userId}`, {
        method: 'DELETE',
      });
      if (error) throw error;
    },
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: getTeamMembersQueryKey() }),
  });

  return {
    ...query,
    updateTeamMember,
    deleteTeamMember,
  };
};
